import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  HeartHandshake, Wallet, Loader2, Pencil, RefreshCw, Ban, CheckCircle2, Copy,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
});

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

// -------- Per-kind config: what makes donations vs refunds different --------
const CONFIGS = {
  donations: {
    testid: 'donations',
    icon: HeartHandshake,
    heading: 'Donations',
    subtitle: 'When attendees cancel, they can choose to gift their payment to ROJO instead of asking for a refund.',
    listKey: 'donations',
    fieldPrefix: 'donation',
    amountField: 'donation_amount',
    statusField: 'donation_status',
    noteField: 'donation_note',
    atField: 'donation_at',
    activeStatus: 'donated',
    endpoint: '/api/retreat/donations',
    statuses: ['donated', 'refunded', 'void'],
    statusStyles: {
      donated: { label: 'Donated', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
      refunded: { label: 'Refunded', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
      void: { label: 'Void', cls: 'bg-red-50 text-red-700 border-red-200' },
    },
    columns: { personLabel: 'Donor', amountLabel: 'Amount' },
    activeCard: {
      title: 'Active donations',
      totalKey: 'total_donated',
      countKey: 'donor_count',
      countLabel: (n) => `from ${n} ${n === 1 ? 'donor' : 'donors'}`,
      cls: 'bg-gradient-to-br from-amber-500 to-orange-500 text-white',
    },
    quickAction: { toStatus: 'refunded', label: 'Refund', icon: CheckCircle2, cls: 'text-gray-500 hover:text-gray-900' },
    restoreAction: { toStatus: 'donated', label: 'Restore', icon: RefreshCw, cls: 'text-amber-600 hover:text-amber-700' },
    editPlaceholder: 'e.g. "Refunded via Venmo 8/12" or "Donor requested anonymous"',
    emptyMessage: 'When someone cancels and chooses to donate their payment, they\u2019ll show up here.',
    emptyTitle: 'No donations yet',
    showVenmo: false,
    showCopyEmail: false,
  },
  refunds: {
    testid: 'refunds',
    icon: Wallet,
    heading: 'Refunds Owed',
    subtitle: 'Attendees who cancelled and chose a refund. Reach out, settle the amount, then mark it paid here.',
    listKey: 'refunds',
    fieldPrefix: 'refund',
    amountField: 'refund_amount',
    statusField: 'refund_status',
    noteField: 'refund_note',
    atField: 'refund_at',
    activeStatus: 'owed',
    endpoint: '/api/retreat/refunds',
    statuses: ['owed', 'paid', 'void'],
    statusStyles: {
      owed: { label: 'Owed', cls: 'bg-red-100 text-red-800 border-red-200' },
      paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
      void: { label: 'Void', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
    },
    columns: { personLabel: 'Person', amountLabel: 'Owed' },
    activeCard: {
      title: 'Owed right now',
      totalKey: 'total_owed',
      countKey: 'owed_count',
      countLabel: (n) => `to ${n} ${n === 1 ? 'person' : 'people'}`,
      cls: 'bg-gradient-to-br from-red-500 to-rose-500 text-white',
    },
    quickAction: { toStatus: 'paid', label: 'Mark paid', icon: CheckCircle2, cls: 'text-emerald-600 hover:text-emerald-700' },
    restoreAction: { toStatus: 'owed', label: 'Reopen', icon: RefreshCw, cls: 'text-red-600 hover:text-red-700' },
    editPlaceholder: 'e.g. "Venmo @username 8/12" or "Check mailed 8/15"',
    emptyMessage: 'When someone cancels and chooses a refund, they\u2019ll show up here.',
    emptyTitle: 'No refunds owed',
    showVenmo: true,
    showCopyEmail: true,
  },
};

// -------- Reusable view --------
const LedgerView = ({ kind }) => {
  const cfg = CONFIGS[kind];
  const [data, setData] = useState({ [cfg.listKey]: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editStatus, setEditStatus] = useState(cfg.activeStatus);
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}${cfg.endpoint}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to load ${kind}`);
    } finally {
      setLoading(false);
    }
  }, [cfg.endpoint, kind]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openEdit = (row) => {
    setEditing(row);
    setEditAmount(String(row[cfg.amountField] ?? ''));
    setEditStatus(row[cfg.statusField] || cfg.activeStatus);
    setEditNote(row[cfg.noteField] || '');
  };
  const closeEdit = () => { if (!saving) setEditing(null); };

  const saveEdit = async () => {
    if (!editing) return;
    const amt = Number(editAmount);
    if (Number.isNaN(amt) || amt < 0) return toast.error('Amount must be a non-negative number.');
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}${cfg.endpoint}/${editing.id}`, {
        [cfg.amountField]: amt,
        [cfg.statusField]: editStatus,
        [cfg.noteField]: editNote.trim() || null,
      }, { headers: getAuthHeaders() });
      toast.success(`${cfg.heading.replace(' Owed','')} updated`);
      setEditing(null);
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (row, status) => {
    try {
      await axios.put(`${BACKEND_URL}${cfg.endpoint}/${row.id}`, {
        [cfg.statusField]: status,
      }, { headers: getAuthHeaders() });
      toast.success(`Marked as ${status}`);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const copyEmail = (email) => navigator.clipboard.writeText(email).then(() => toast.success(`Copied ${email}`));

  const rows = data[cfg.listKey] || [];
  const nonActiveCount = rows.filter((r) => r[cfg.statusField] !== cfg.activeStatus).length;
  const IconHead = cfg.icon;
  const QuickIcon = cfg.quickAction.icon;
  const RestoreIcon = cfg.restoreAction.icon;

  return (
    <div>
      {/* Header + refresh */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <IconHead className="w-7 h-7 text-wine" />
            {cfg.heading}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{cfg.subtitle}</p>
        </div>
        <Button
          onClick={fetchList}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid={`${cfg.testid}-refresh`}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className={`border-0 shadow-sm ${cfg.activeCard.cls}`} data-testid={`${cfg.testid}-active-card`}>
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-widest text-white/80 uppercase">{cfg.activeCard.title}</p>
            <p className="text-3xl font-bold mt-2">${(data[cfg.activeCard.totalKey] || 0).toLocaleString()}</p>
            <p className="text-xs text-white/80 mt-1">{cfg.activeCard.countLabel(data[cfg.activeCard.countKey] || 0)}</p>
          </CardContent>
        </Card>
        {kind === 'refunds' ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Already paid</p>
              <p className="text-3xl font-bold text-emerald-700 mt-2">${(data.total_paid || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">settled out of cycle</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Refunded / void</p>
              <p className="text-3xl font-bold text-gray-700 mt-2">{nonActiveCount}</p>
              <p className="text-xs text-gray-500 mt-1">no longer counted in totals</p>
            </CardContent>
          </Card>
        )}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Total records</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{rows.length}</p>
            <p className="text-xs text-gray-500 mt-1">includes settled &amp; voided</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-sm text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center" data-testid={`${cfg.testid}-empty`}>
              <IconHead className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">{cfg.emptyTitle}</p>
              <p className="text-xs text-gray-500 mt-1">{cfg.emptyMessage}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3">{cfg.columns.personLabel}</th>
                    <th className="px-4 py-3">{cfg.columns.amountLabel}</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Note</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => {
                    const s = cfg.statusStyles[r[cfg.statusField]] || cfg.statusStyles[cfg.activeStatus];
                    const isActive = r[cfg.statusField] === cfg.activeStatus;
                    return (
                      <tr key={r.id} className="hover:bg-gray-50/60" data-testid={`${cfg.testid}-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900">{r.first_name} {r.last_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500 truncate">{r.email}</span>
                            {cfg.showCopyEmail && (
                              <button
                                type="button"
                                onClick={() => copyEmail(r.email)}
                                className="text-gray-300 hover:text-gray-700"
                                title="Copy email"
                                data-testid={`${cfg.testid}-copy-email-${r.id}`}
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          {cfg.showVenmo && r.venmo ? (
                            <p className="text-xs text-violet-600 mt-0.5">Venmo: {r.venmo}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-gray-900">${Number(r[cfg.amountField] || 0).toFixed(0)}</p>
                          {r.amount_paid != null && Number(r.amount_paid) !== Number(r[cfg.amountField]) ? (
                            <p className="text-xs text-gray-400">paid ${Number(r.amount_paid).toFixed(0)}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`${s.cls} font-medium`}>{s.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                          {fmtDate(r[cfg.atField])}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 max-w-[220px] truncate italic">
                          {r[cfg.noteField] || (r.cancellation_reason ? `"${r.cancellation_reason}"` : '—')}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {isActive ? (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => quickStatus(r, cfg.quickAction.toStatus)}
                              className={`h-7 px-2 text-xs ${cfg.quickAction.cls}`}
                              data-testid={`${cfg.testid}-quick-${r.id}`}
                            >
                              <QuickIcon className="w-3 h-3 mr-1" />
                              {cfg.quickAction.label}
                            </Button>
                          ) : (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => quickStatus(r, cfg.restoreAction.toStatus)}
                              className={`h-7 px-2 text-xs ${cfg.restoreAction.cls}`}
                              data-testid={`${cfg.testid}-restore-${r.id}`}
                            >
                              <RestoreIcon className="w-3 h-3 mr-1" />
                              {cfg.restoreAction.label}
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => openEdit(r)}
                            className="h-7 px-2 text-xs text-gray-500 hover:text-gray-900"
                            data-testid={`${cfg.testid}-edit-${r.id}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => quickStatus(r, 'void')}
                            className="h-7 px-2 text-xs text-gray-400 hover:text-red-600"
                            title="Void (never count)"
                            data-testid={`${cfg.testid}-void-${r.id}`}
                          >
                            <Ban className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) closeEdit(); }}>
        <DialogContent className="max-w-md" data-testid={`${cfg.testid}-edit-modal`}>
          <DialogHeader>
            <DialogTitle>Edit {cfg.fieldPrefix}</DialogTitle>
            <DialogDescription>
              {editing ? `${editing.first_name} ${editing.last_name} · ${editing.email}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-1.5">Amount ($)</label>
              <Input
                type="number" min="0" step="0.01"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                data-testid={`${cfg.testid}-edit-amount`}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-1.5">Status</label>
              <div className="grid grid-cols-3 gap-2">
                {cfg.statuses.map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setEditStatus(st)}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                      editStatus === st
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                    data-testid={`${cfg.testid}-edit-status-${st}`}
                  >
                    {st}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-1.5">Note (optional)</label>
              <Textarea
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder={cfg.editPlaceholder}
                rows={3}
                className="resize-none"
                data-testid={`${cfg.testid}-edit-note`}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeEdit} disabled={saving} data-testid={`${cfg.testid}-edit-cancel`}>
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={saving}
              className="bg-gray-900 hover:bg-gray-800 text-white"
              data-testid={`${cfg.testid}-edit-save`}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// -------- Ledger shell with two tabs --------
const LedgerPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  // Priority: ?tab=X query param, then infer from URL path (/refunds vs /donations), then default donations.
  const pathIndicatesRefunds = /\/refunds(\/|$|\?)/.test(location.pathname);
  const initial = params.get('tab') === 'refunds' || pathIndicatesRefunds ? 'refunds' : 'donations';
  const [tab, setTab] = useState(initial);

  const setTabAndUrl = (t) => {
    setTab(t);
    navigate(`?tab=${t}`, { replace: true });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ledger-page">
      {/* Tab switcher */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 mb-6">
        {[
          { key: 'donations', label: 'Donations', icon: HeartHandshake },
          { key: 'refunds', label: 'Refunds Owed', icon: Wallet },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTabAndUrl(t.key)}
              className={`px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 transition-colors ${
                active ? 'bg-wine text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid={`ledger-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      <LedgerView kind={tab} />
    </div>
  );
};

export default LedgerPage;
